import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';

/**
 * Small, quiet motion for the public site. Everything here does nothing when
 * the visitor has asked the system to reduce motion, and nothing here moves
 * layout: only opacity and `translate`, so there is no layout shift.
 */

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Stagger for the n-th item of a list that reveals together. */
export const revealDelay = (i: number): CSSProperties => ({ '--reveal-delay': `${Math.min(i, 3) * 60}ms` }) as CSSProperties;

/**
 * Fades `[data-reveal]` elements up the first time they scroll into view.
 * Mounted once by the public layout; it also picks up elements that render
 * later (content arrives after the first paint). The hidden starting state is
 * scoped to `html.js-reveal`, so if this never runs nothing stays invisible.
 */
export function useRevealOnScroll(root: React.RefObject<HTMLElement>, key: string): void {
  useEffect(() => {
    const el = root.current;
    if (!el || prefersReducedMotion() || !('IntersectionObserver' in window)) return;
    document.documentElement.classList.add('js-reveal');

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.classList.add('is-revealed');
          io.unobserve(e.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );
    const watch = (scope: ParentNode) =>
      scope.querySelectorAll<HTMLElement>('[data-reveal]:not(.is-revealed)').forEach((n) => io.observe(n));
    watch(el);
    const mo = new MutationObserver((records) => {
      for (const r of records) r.addedNodes.forEach((n) => n instanceof HTMLElement && (n.matches('[data-reveal]') ? io.observe(n) : watch(n)));
    });
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [root, key]);
}

/**
 * Types `text` out one letter at a time, with a caret that blinks and then
 * fades. Every letter is in the DOM from the start (hidden until typed), so
 * the heading has its final size and line breaks on the first frame, and its
 * text content is the plain words, for search engines, screen readers and
 * copy-paste.
 *
 * The caret is one element placed by measuring the last typed letter, so it
 * follows the text across line breaks. After a space it waits at the start
 * of the next word, which is on the next line when the line wraps there.
 */
export function Typewriter({ text, speed = 110, delay = 300 }: { text: string; speed?: number; delay?: number }) {
  const [shown, setShown] = useState(() => (prefersReducedMotion() ? text.length : 0));
  const [caret, setCaret] = useState<{ left: number; top: number } | null>(null);
  const box = useRef<HTMLSpanElement>(null);
  const letters = useRef<Array<HTMLSpanElement | null>>([]);
  const done = shown >= text.length;

  useEffect(() => {
    if (prefersReducedMotion()) return setShown(text.length);
    setShown(0);
    let i = 0;
    let timer = window.setTimeout(function tick() {
      i += 1;
      setShown(i);
      if (i < text.length) timer = window.setTimeout(tick, speed);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [text, speed, delay]);

  useLayoutEffect(() => {
    const place = () => {
      const root = box.current;
      if (!root || shown === 0) return setCaret(null);
      const origin = root.getBoundingClientRect();
      const em = parseFloat(getComputedStyle(root).fontSize);
      const last = letters.current[shown - 1];
      const next = letters.current[shown];
      // After a space, sit where the next word starts (maybe the next line).
      const anchor = text[shown - 1] === ' ' && next ? next.getBoundingClientRect() : null;
      const r = anchor ?? last?.getBoundingClientRect();
      if (!r) return;
      setCaret({ left: (anchor ? r.left : r.right) - origin.left + 0.05 * em, top: r.top - origin.top + 0.32 * em });
    };
    place();
    const ro = new ResizeObserver(place);
    if (box.current) ro.observe(box.current);
    return () => ro.disconnect();
  }, [shown, text]);

  return (
    <span ref={box} className="relative block">
      {[...text].map((ch, i) => (
        <span key={i} ref={(el) => void (letters.current[i] = el)} className={i < shown ? undefined : 'text-transparent'}>
          {ch}
        </span>
      ))}
      {caret && <span aria-hidden className={`typewriter-caret ${done ? 'is-done' : ''}`} style={caret} />}
    </span>
  );
}

/**
 * A figure to count up: its number, whether it is written with a dot every
 * three digits ("325.000+", as Vietnamese writes it) and what follows it.
 * Null for a year ("2019") or anything that does not start with a number.
 */
export function parseFigure(value: string): { target: number; grouped: boolean; rest: string } | null {
  const m = value.match(/^(\d{1,3}(?:\.\d{3})+|\d{1,6})(.*)$/);
  if (!m) return null;
  const grouped = m[1]!.includes('.');
  const target = Number(m[1]!.replace(/\./g, ''));
  const isYear = !grouped && m[1]!.length === 4 && target >= 1900 && target <= 2100;
  return isYear || target <= 0 ? null : { target, grouped, rest: m[2] ?? '' };
}

/** A count on its way up, written the way the figure is ("12.345"). */
export function formatFigure(n: number, grouped: boolean): string {
  return grouped ? String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.') : String(n);
}

/**
 * Counts a figure like "150+" or "325.000+" up from zero when it first comes
 * into view. Years ("2019") and anything that is not a leading number are shown
 * as is. Screen readers hear the final figure only.
 */
export function CountUp({ value, duration = 1200 }: { value: string; duration?: number }) {
  const fig = parseFigure(value);
  const target = fig?.target ?? NaN;
  const animate = Boolean(fig);

  const ref = useRef<HTMLSpanElement>(null);
  const [n, setN] = useState<number | null>(animate && !prefersReducedMotion() ? 0 : null);

  useEffect(() => {
    if (n === null || !ref.current || !('IntersectionObserver' in window)) return setN(null);
    let raf = 0;
    const io = new IntersectionObserver(([e]) => {
      if (!e?.isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const step = (t: number) => {
        const k = Math.min(1, (t - start) / duration);
        const eased = 1 - Math.pow(1 - k, 3);
        setN(Math.round(target * eased));
        if (k < 1) raf = requestAnimationFrame(step);
        else setN(null);
      };
      raf = requestAnimationFrame(step);
    }, { threshold: 0.6 });
    io.observe(ref.current);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
    // Runs once per value; n only matters for the initial decision.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  if (!animate) return <>{value}</>;
  // One text node, reserved to the final width, so nothing jumps and the
  // text content is always a real figure.
  // Screen readers get the final figure as text: an aria-label on a plain span is
  // not allowed (axe: aria-prohibited-attr) and was skipped.
  return (
    <span ref={ref} className="inline-block tabular-nums" style={{ minWidth: `${value.length}ch` }}>
      <span aria-hidden>{n === null ? value : `${formatFigure(n, fig!.grouped)}${fig!.rest}`}</span>
      <span className="sr-only">{value}</span>
    </span>
  );
}
